import React from "react";
import {
  Receipt,
  User as UserIcon,
  Building2,
  CreditCard,
  Tag,
  Star,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Hash,
  CheckCircle2,
  Clock,
  Download,
  XCircle,
} from "lucide-react";
import { fmtINR, fmtINR2, fmtDate } from "../utils";

// Rich invoice detail — built from the full invoice envelope the
// /api/sales-drill-down/invoice/:id endpoint returns.
//
// Sections:
//   - Header strip      (invoice no, date, status pills, print/export)
//   - Two-column body:
//       LEFT  → Party card + Store card
//       RIGHT → Totals card (with discount breakdown including the new
//               soit_coupon_discount / soit_points_discount /
//               soit_scheme_discount from the loyalty PR)
//     + Payment-modes card (sales only, when available)
//   - Items table (with per-line GST + discount columns)
//   - Footer: invoice total bar
export default function InvoiceDetail({ data, searchTerm }) {
  const { invoice, items, party, store, payment_modes, metric } = data;
  const isSales = metric === "sales";
  const partyLabel = isSales ? "Customer" : "Vendor";
  const tone = isSales
    ? {
        text: "text-[var(--dashboard-green-dark)]",
        bg: "bg-[var(--dashboard-bg-success)]",
        border: "border-[var(--dashboard-bg-success)]",
        pill: "bg-[var(--dashboard-bg-success)] text-[var(--dashboard-green-dark)]",
      }
    : {
        text: "text-[var(--dashboard-warning-dark)]",
        bg: "bg-[var(--dashboard-bg-warning)]",
        border: "border-[var(--dashboard-bg-warning)]",
        pill: "bg-[var(--dashboard-bg-warning)] text-[var(--dashboard-warning-dark)]",
      };

  // Field-name aliasing across the invoice shapes we accept:
  //   sales bills           → sot_*  (sales_order — what /bills shows)
  //   purchase / GRN        → gort_* (goods_order_receipt_table)
  //   legacy soit_* / poit_*           kept as fallbacks just in case.
  const invNo =
    invoice.sot_custom_invoice_number ||
    invoice.sot_invoice_number ||
    invoice.gort_invoice_number ||
    invoice.soit_invoice_number ||
    invoice.poit_invoice_number ||
    invoice.sot_id ||
    invoice.gort_id ||
    invoice.soit_id ||
    invoice.poit_id;
  const invDate =
    invoice.sot_custom_invoice_date ||
    invoice.gort_order_date ||
    invoice.gort_document_date ||
    invoice.soit_order_date ||
    invoice.poit_order_date ||
    invoice.created_date;
  const subTotal = Number(
    invoice.sot_sub_total || invoice.gort_sub_total || invoice.soit_sub_total || invoice.poit_sub_total || 0
  );
  const gst = Number(
    invoice.sot_total_gst || invoice.gort_total_gst || invoice.soit_total_gst || invoice.poit_total_gst || 0
  );
  const totalDiscount = Number(
    invoice.sot_total_discount || invoice.gort_total_discount || invoice.soit_total_discount || invoice.poit_total_discount || 0
  );
  const totalAmount = Number(
    invoice.sot_total_amount || invoice.gort_total_amount || invoice.soit_total_amount || invoice.poit_total_amount || 0
  );
  // Loyalty + coupon + scheme discounts only land on bills (sales_order),
  // never on GRNs. The denorm columns came in via the loyalty PR.
  const couponDisc = Number(invoice.sot_coupon_discount || invoice.soit_coupon_discount || 0);
  const schemeDisc = Number(invoice.sot_scheme_discount || invoice.soit_scheme_discount || 0);
  const pointsDisc = Number(invoice.sot_points_discount || invoice.soit_points_discount || 0);
  const orderStatus =
    invoice.sot_order_status || invoice.gort_order_status || invoice.soit_order_status || invoice.poit_order_status;
  const paymentStatus =
    invoice.sot_payment_status || invoice.gort_payment_status || invoice.soit_payment_status || invoice.poit_payment_status;
  const remarks =
    invoice.sot_remarks || invoice.remarks || invoice.gort_remarks || invoice.soit_remarks || invoice.poit_remarks;

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className={`bg-white border ${tone.border} rounded-xl shadow-sm overflow-hidden`}>
        <div className={`px-5 py-3 ${tone.bg} border-b ${tone.border} flex items-center justify-between gap-3 flex-wrap`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${tone.bg} ${tone.text} flex items-center justify-center border ${tone.border}`}>
              <Receipt size={18} />
            </div>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-[var(--dashboard-gray-500)]">
                {isSales ? "Sales Invoice" : "Purchase Invoice"}
              </div>
              <div className="font-mono text-[16px] font-bold text-[var(--dashboard-gray-900)]">{invNo}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold text-[var(--dashboard-gray-500)] bg-white border border-[var(--dashboard-gray-200)] rounded-md hover:border-[var(--dashboard-gray-400)] hover:text-[var(--dashboard-gray-900)]"
              onClick={() =>
                exportCsv({
                  invNo,
                  invDate,
                  party,
                  partyLabel,
                  store,
                  items,
                  subTotal,
                  gst,
                  totalDiscount,
                  totalAmount,
                  couponDisc,
                  schemeDisc,
                  pointsDisc,
                })
              }
            >
              <Download size={13} /> Export CSV
            </button>
          </div>
        </div>
        <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <KV icon={<Calendar size={13} />} label="Date" value={fmtDate(invDate)} />
          <KV
            icon={<Hash size={13} />}
            label="Status"
            value={<StatusBadge status={orderStatus || "open"} kind="order" />}
          />
          <KV
            icon={<CreditCard size={13} />}
            label="Payment"
            value={<StatusBadge status={paymentStatus || "—"} kind="payment" />}
          />
          <KV
            icon={<Building2 size={13} />}
            label="Store"
            value={
              <span className="font-mono font-semibold">{store?.store_code || "—"}</span>
            }
          />
        </div>
      </div>

      {/* Body — 2 cols on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* LEFT — Party + Store + Items */}
        <div className="space-y-4">
          {/* Party card */}
          <Card title={partyLabel} icon={<UserIcon size={15} />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name" value={party?.cmr_name || "—"} bold />
              <Field
                label="Phone"
                value={party?.cmr_phone_number || "—"}
                mono
                icon={<Phone size={11} />}
              />
              <Field
                label="Email"
                value={party?.cmr_email || "—"}
                icon={<Mail size={11} />}
              />
              <Field
                label="City"
                value={[party?.cmr_city, party?.cmr_state].filter(Boolean).join(", ") || "—"}
                icon={<MapPin size={11} />}
              />
              {party?.cmr_address ? (
                <Field
                  label="Address"
                  value={party.cmr_address}
                  colSpan
                />
              ) : null}
              {!isSales && party?.is_vendor_cmr ? (
                <Field
                  label="Vendor balance"
                  value={fmtINR2(party.cmr_balance || 0)}
                  mono
                />
              ) : null}
            </div>
          </Card>

          {/* Store card */}
          {store ? (
            <Card title="Originating store" icon={<Building2 size={15} />}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Code" value={store.store_code || "—"} mono bold />
                <Field label="Category" value={store.store_type || "Uncategorized"} />
                <Field label="City" value={store.shop_city || "—"} />
                <Field label="State" value={store.shop_state || "—"} />
                {store.shop_address ? (
                  <Field label="Address" value={store.shop_address} colSpan />
                ) : null}
              </div>
            </Card>
          ) : null}

          {/* Items table */}
          <div className="bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--dashboard-gray-200)]">
              <h3 className="text-[14px] font-bold text-[var(--dashboard-gray-900)]">
                Line items <span className="text-[var(--dashboard-gray-500)] font-normal">({items.length})</span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--dashboard-bg-table-head)] border-b border-[var(--dashboard-gray-200)]">
                    <Th>Item</Th>
                    <Th>HSN / Batch</Th>
                    <Th className="text-right">Qty</Th>
                    <Th className="text-right">Rate</Th>
                    <Th className="text-right">Disc.</Th>
                    <Th className="text-right">GST</Th>
                    <Th className="text-right">Amount</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const term = (searchTerm || "").trim().toLowerCase();
                    const hl =
                      term && String(it.item_name || "").toLowerCase().indexOf(term) !== -1;
                    return (
                      <tr
                        key={(it.item_id || "") + "_" + idx}
                        className={`border-b border-[var(--dashboard-gray-100)] last:border-b-0 ${hl ? "bg-[var(--dashboard-bg-warning)]" : ""}`}
                      >
                        <Td>
                          <div className="font-semibold text-[var(--dashboard-gray-900)]">
                            {it.item_name || "—"}
                          </div>
                          {it.item_code ? (
                            <div className="text-[11.5px] text-[var(--dashboard-gray-500)] font-mono">
                              {it.item_code}
                            </div>
                          ) : null}
                        </Td>
                        <Td>
                          <div className="text-[12px] text-[var(--dashboard-gray-500)] font-mono">
                            {it.item_hsn || it.hsn || "—"}
                          </div>
                          {it.item_batch_number ? (
                            <div className="text-[11.5px] text-[var(--dashboard-gray-400)] font-mono">
                              {it.item_batch_number}
                            </div>
                          ) : null}
                        </Td>
                        <Td className="text-right font-mono">{it.item_quantity}</Td>
                        <Td className="text-right font-mono">{fmtINR(it.item_unit_price)}</Td>
                        <Td className="text-right font-mono text-[var(--dashboard-gray-500)]">
                          {Number(it.item_discount_amount) > 0
                            ? "− " + fmtINR(it.item_discount_amount)
                            : "—"}
                        </Td>
                        <Td className="text-right font-mono text-[var(--dashboard-gray-500)]">
                          {Number(it.item_tax_amount) > 0
                            ? fmtINR(it.item_tax_amount)
                            : "—"}
                        </Td>
                        <Td className="text-right font-mono font-bold">
                          {fmtINR(it.item_total_amount)}
                        </Td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-5 py-10 text-center text-[13px] text-[var(--dashboard-gray-500)]">
                        No line items recorded for this invoice.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT — Totals + Payment modes */}
        <div className="space-y-4">
          <Card title="Bill totals" icon={<Receipt size={15} />}>
            <TotalRow label="Subtotal" value={fmtINR2(subTotal)} />
            <TotalRow label="GST" value={fmtINR2(gst)} />
            {totalDiscount > 0 && (
              <TotalRow
                label="Manual discount"
                value={"− " + fmtINR2(totalDiscount)}
                muted
              />
            )}
            {couponDisc > 0 && (
              <TotalRow
                icon={<Tag size={11} />}
                label="Coupon"
                value={"− " + fmtINR2(couponDisc)}
                accent="text-[var(--dashboard-green-dark)]"
              />
            )}
            {schemeDisc > 0 && (
              <TotalRow
                icon={<Tag size={11} />}
                label="Scheme"
                value={"− " + fmtINR2(schemeDisc)}
                accent="text-[var(--dashboard-purple)]"
              />
            )}
            {pointsDisc > 0 && (
              <TotalRow
                icon={<Star size={11} />}
                label="Loyalty points"
                value={"− " + fmtINR2(pointsDisc)}
                accent="text-[var(--dashboard-warning-dark)]"
              />
            )}
            <div className="border-t border-[var(--dashboard-gray-200)] mt-3 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] font-bold text-[var(--dashboard-gray-500)] uppercase tracking-wide">
                  Total
                </span>
                <span className={`font-mono text-[22px] font-bold ${tone.text}`}>
                  {fmtINR2(totalAmount)}
                </span>
              </div>
            </div>
          </Card>

          {isSales && payment_modes && payment_modes.length > 0 ? (
            <Card title="Payment modes" icon={<CreditCard size={15} />}>
              {payment_modes.map((pm, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-1.5 border-b border-[var(--dashboard-gray-100)] last:border-b-0"
                >
                  <span className="text-[13px] font-semibold text-[var(--dashboard-gray-900)]">
                    {pm.payment_mode_name || "Unknown"}
                  </span>
                  <span className="font-mono text-[13px]">{fmtINR2(pm.payment_amount)}</span>
                </div>
              ))}
            </Card>
          ) : null}

          {remarks ? (
            <Card title="Remarks" icon={<Hash size={15} />}>
              <p className="text-[13px] text-[var(--dashboard-gray-500)] leading-relaxed">{remarks}</p>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ----------------- small primitives ----------------- */
function Card({ title, icon, children }) {
  return (
    <div className="bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-sm">
      <div className="px-5 py-3 border-b border-[var(--dashboard-gray-100)] flex items-center gap-2">
        <span className="text-[var(--dashboard-gray-500)]">{icon}</span>
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-[var(--dashboard-gray-500)]">
          {title}
        </h3>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}
function Field({ label, value, mono, bold, icon, colSpan }) {
  return (
    <div className={colSpan ? "col-span-full" : ""}>
      <div className="text-[10.5px] font-bold uppercase tracking-wide text-[var(--dashboard-gray-500)] flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div
        className={`mt-1 text-[13.5px] text-[var(--dashboard-gray-900)] ${mono ? "font-mono" : ""} ${bold ? "font-semibold" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}
function KV({ icon, label, value }) {
  return (
    <div>
      <div className="text-[10.5px] font-bold uppercase tracking-wide text-[var(--dashboard-gray-500)] flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-[13.5px] font-semibold text-[var(--dashboard-gray-900)]">{value}</div>
    </div>
  );
}
function TotalRow({ label, value, muted, accent, icon }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-[13px] font-semibold flex items-center gap-1.5 ${muted ? "text-[var(--dashboard-gray-400)]" : "text-[var(--dashboard-gray-500)]"}`}>
        {icon}
        {label}
      </span>
      <span className={`font-mono text-[13.5px] font-semibold ${accent || (muted ? "text-[var(--dashboard-gray-400)]" : "text-[var(--dashboard-gray-900)]")}`}>
        {value}
      </span>
    </div>
  );
}
function StatusBadge({ status, kind }) {
  const s = String(status || "").toLowerCase();
  const orderTone = {
    open: { bg: "bg-[var(--dashboard-bg-success)]", text: "text-[var(--dashboard-success)]", icon: <CheckCircle2 size={10} /> },
    paid: { bg: "bg-[var(--dashboard-bg-success)]", text: "text-[var(--dashboard-success)]", icon: <CheckCircle2 size={10} /> },
    pending: { bg: "bg-[var(--dashboard-bg-warning)]", text: "text-[var(--dashboard-warning-dark)]", icon: <Clock size={10} /> },
    draft: { bg: "bg-[var(--dashboard-gray-100)]", text: "text-[var(--dashboard-gray-500)]", icon: <Clock size={10} /> },
    cancelled: { bg: "bg-[#FEF2F2]", text: "text-[var(--dashboard-danger)]", icon: <XCircle size={10} /> },
    partial: { bg: "bg-[var(--dashboard-bg-warning)]", text: "text-[var(--dashboard-warning-dark)]", icon: <Clock size={10} /> },
  };
  const t = orderTone[s] || { bg: "bg-[var(--dashboard-gray-100)]", text: "text-[var(--dashboard-gray-500)]", icon: null };
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${t.bg} ${t.text}`}>
      {t.icon}
      {status || "—"}
    </span>
  );
}
function Th({ children, className = "" }) {
  return (
    <th
      className={`text-left text-[11px] font-bold uppercase tracking-wide text-[var(--dashboard-gray-500)] px-4 py-2.5 ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-2.5 text-[13.5px] align-top text-[var(--dashboard-gray-900)] ${className}`}>
      {children}
    </td>
  );
}

/* ----------------- export ----------------- */
// Takes the already-aliased values from InvoiceDetail's top-level resolution
// (the same numbers the user sees on screen). Earlier version still read
// soit_* / poit_* keys directly off the raw invoice row — that broke the
// moment we switched the sales source to sales_order (sot_*) and the GRN
// source to goods_order_receipt_table (gort_*), producing a CSV with the
// invoice-no/date/totals rows empty.
function exportCsv({
  invNo,
  invDate,
  party,
  partyLabel,
  store,
  items,
  subTotal,
  gst,
  totalDiscount,
  totalAmount,
  couponDisc,
  schemeDisc,
  pointsDisc,
}) {
  const lines = [];
  const headerRow = (label, value) => lines.push([csvEsc(label), csvEsc(value)].join(","));

  headerRow("Invoice", invNo || "");
  headerRow("Date", fmtDate(invDate));
  headerRow(partyLabel, party?.cmr_name || "");
  headerRow("Phone", party?.cmr_phone_number || "");
  if (party?.cmr_email) headerRow("Email", party.cmr_email);
  if (party?.cmr_address) headerRow("Address", party.cmr_address);
  headerRow("Store", store?.store_code || "");
  if (store?.shop_city || store?.shop_state) {
    headerRow(
      "Store location",
      [store?.shop_city, store?.shop_state].filter(Boolean).join(", ")
    );
  }
  lines.push("");

  lines.push(["Item", "Code", "HSN", "Batch", "Qty", "Rate", "Discount", "GST", "Amount"].join(","));
  (items || []).forEach((it) => {
    lines.push(
      [
        csvEsc(it.item_name || ""),
        csvEsc(it.item_code || ""),
        csvEsc(it.item_hsn || it.hsn || ""),
        csvEsc(it.item_batch_number || ""),
        it.item_quantity ?? "",
        it.item_unit_price ?? "",
        it.item_discount_amount ?? "",
        it.item_tax_amount ?? "",
        it.item_total_amount ?? "",
      ].join(",")
    );
  });
  lines.push("");

  // Totals block — only emit discount breakdown rows when they exist so the
  // CSV stays compact for plain bills.
  headerRow("Subtotal", fmtNum(subTotal));
  headerRow("GST", fmtNum(gst));
  if (Number(totalDiscount) > 0) headerRow("Manual discount", fmtNum(totalDiscount));
  if (Number(couponDisc) > 0) headerRow("Coupon discount", fmtNum(couponDisc));
  if (Number(schemeDisc) > 0) headerRow("Scheme discount", fmtNum(schemeDisc));
  if (Number(pointsDisc) > 0) headerRow("Loyalty points discount", fmtNum(pointsDisc));
  headerRow("Total", fmtNum(totalAmount));

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${invNo || "invoice"}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
function csvEsc(s) {
  if (s === null || s === undefined) return "";
  const str = String(s);
  if (/[,"\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
  return str;
}
function fmtNum(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return v.toFixed(2);
}
